import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { brandColors as colors } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { dateSortValue, formatDate, formatInteger, formatMoney } from "@/lib/format";
import {
  AdminTableControls,
  AdminTableFilterSelect,
  SortableTh,
  useAdminTable,
} from "@/components/admin/AdminTableControls";

type Order = {
  id: string;
  name: string;
  email: string;
  totalCents: number;
  status: "pending" | "paid" | "shipped" | "delivered" | "refunded";
  trackingNumber: string | null;
  createdAt: string;
  itemCount: number;
  itemNames: string[];
  stripePaymentId: string | null;
};

type OrderSortKey = "date" | "customer" | "items" | "total" | "status" | "tracking";

function orderSearchText(order: Order) {
  return [
    order.name,
    order.email,
    order.status,
    order.trackingNumber,
    ...(order.itemNames ?? []),
    order.stripePaymentId,
    formatMoney(order.totalCents),
    formatDate(order.createdAt),
  ]
    .filter(Boolean)
    .join(" ");
}

const orderSorters: Record<OrderSortKey, (row: Order) => string | number> = {
  date: (row) => dateSortValue(row.createdAt),
  customer: (row) => row.name || row.email,
  items: (row) => row.itemCount,
  total: (row) => row.totalCents,
  status: (row) => row.status,
  tracking: (row) => row.trackingNumber ?? "",
};

export default function AdminOrders() {
  const [list, setList] = useState<Order[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    api
      .get<{ orders: Order[] }>("/api/admin/orders")
      .then((r) => setList(r.orders))
      .finally(() => setLoaded(true));
  }, []);

  const statusOptions = useMemo(
    () =>
      ["all", ...Array.from(new Set(list.map((row) => row.status))).sort()].map((value) => ({
        value,
        label: value === "all" ? "All statuses" : value,
      })),
    [list],
  );
  const filters = useMemo(
    () => [(row: Order) => statusFilter === "all" || row.status === statusFilter],
    [statusFilter],
  );
  const {
    rows: tableRows,
    sort,
    requestSort,
  } = useAdminTable<Order, OrderSortKey>({
    rows: list,
    search,
    initialSort: { key: "date", direction: "desc" },
    sorters: orderSorters,
    searchText: orderSearchText,
    filters,
  });

  return (
    <section>
      <style>{css}</style>
      <div className="hdr">
        <h1 className="aflt-admin-title">Orders</h1>
      </div>

      {!loaded ? (
        <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>Loading…</p>
      ) : (
        <>
          <AdminTableControls
            search={search}
            onSearchChange={setSearch}
            placeholder="Search orders, customers, events, tracking..."
            totalCount={list.length}
            resultCount={tableRows.length}
          >
            <AdminTableFilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={statusOptions}
            />
          </AdminTableControls>
          {list.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No orders yet.</p>
          ) : tableRows.length === 0 ? (
            <p style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>No orders match those filters.</p>
          ) : (
            <table className="paper-table">
              <thead>
                <tr>
                  <SortableTh label="Date" sortKey="date" sort={sort} onSort={requestSort} />
                  <SortableTh label="Customer" sortKey="customer" sort={sort} onSort={requestSort} />
                  <SortableTh label="Items" sortKey="items" sort={sort} onSort={requestSort} />
                  <SortableTh label="Total" sortKey="total" sort={sort} onSort={requestSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={requestSort} />
                  <SortableTh label="Tracking" sortKey="tracking" sort={sort} onSort={requestSort} />
                </tr>
              </thead>
              <tbody>
                {tableRows.map((o) => (
                  <tr key={o.id}>
                    <td style={{ whiteSpace: "nowrap" }}>{formatDate(o.createdAt)}</td>
                    <td>
                      <strong>{o.name}</strong>
                      <span className="sub">{o.email}</span>
                    </td>
                    <td>{formatInteger(o.itemCount)}<span className="sub">{o.itemNames?.join(", ")}</span></td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {formatMoney(o.totalCents)}
                      {o.stripePaymentId?.startsWith("pi_") && <a className="sub" href={`https://dashboard.stripe.com/payments/${o.stripePaymentId}`} target="_blank" rel="noopener noreferrer">View in Stripe</a>}
                    </td>
                    <td>
                      <span className={`badge badge-${o.status}`}>{o.status}</span>
                    </td>
                    <td style={{ fontFamily: fonts.body, fontSize: 13, color: `${colors.navy}bb` }}>
                      {o.trackingNumber ?? <span style={{ opacity: 0.4 }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </section>
  );
}

const css = `
  .hdr { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-bottom: 28px; }

  .paper-table { width: 100%; border-collapse: separate; border-spacing: 0; background: #fff; border: 1.5px dashed rgba(201,147,76,0.40); border-radius: 16px; overflow: hidden; }
  .paper-table th, .paper-table td { text-align: left; padding: 0.7rem 0.9rem; border-bottom: 1px dashed rgba(201,147,76,0.18); font-family: ${fonts.body}; vertical-align: middle; }
  .paper-table thead th { background: ${colors.blushLight}; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.18em; color: rgba(8,45,61,0.7); border-bottom: 1px solid rgba(201,147,76,0.15); }
  .paper-table tbody tr:last-child td { border-bottom: none; }
  .paper-table tbody td { font-size: 13.5px; color: rgba(8,45,61,0.85); }
  .paper-table .sub { display: block; font-size: 11px; color: rgba(8,45,61,0.47); }

  .badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 11px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; font-family: ${fonts.body}; }
  .badge-pending   { background: rgba(201,147,76,0.15); color: rgba(8,45,61,0.75); }
  .badge-paid      { background: rgba(60,179,113,0.15); color: #2d7a52; }
  .badge-shipped   { background: rgba(100,149,237,0.15); color: #3155a0; }
  .badge-delivered { background: rgba(60,179,113,0.22); color: #1e6640; }
  .badge-refunded  { background: rgba(212,96,106,0.15); color: ${colors.berryRose}; }
`;
